// All Documents - Main Component

// Aggregated view of all documents uploaded across the system.
// Supports list view (default) and folder view modes.
// Includes search, sort, filter, and document preview.

// react
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// sse
import { useSSE } from '../../../../utils/sse/useSSE';
import type { SSENotification } from '../../../../utils/sse/useSSE';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	alpha,
	Box,
	Button,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	Divider,
	Grid,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountTree as IconActivity,
	Article as IconDocument,
	AutoAwesome as IconAI,
	Business as IconBeneficiary,
	CloudUpload as IconUpload,
	Description as IconContract,
	Download as IconDownload,
	Edit as IconEdit,
	Folder as IconFolder,
	FolderOpen as IconFolderOpen,
	Image as IconImage,
	InsertDriveFile as IconFile,
	List as IconList,
	OpenInNew as IconOpenDocument,
	Payment as IconPayment,
	PictureAsPdf as IconPdf,
	Receipt as IconInvoice,
	SwapVert as IconTransaction,
	TableChart as IconXlsx,
	ViewModule as IconGrid,
} from '@mui/icons-material';

// components
import { SearchFilterBar } from '../../../ui/SearchFilterBar/SearchFilterBar';

// context
import { ContextProject } from '../../Project/Project.context';
import {
	ColumnDef,
	DataTable,
	FilterConfig as DTFilterConfig,
	RowAction,
	type SortDirection as DTSortDirection,
} from '../../../ui/DataTable/DataTable';
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// dialogs
import { DialogDocumentViewer } from '../../../dialogs/DialogDocumentViewer/DialogDocumentViewer';
import { DialogUploadDocument } from '../../../dialogs/DialogUploadDocument/DialogUploadDocument';

// hook - AI documents API
import { SORT_KEY_MAP, useAiDocuments } from '../../../../hooks/useAiDocuments';

// AI API
import { fetchAiDocumentResultsBulk, updateAiDocument } from '../../../../services/aiApi';
import type { AiDocumentResultBlob } from './documentTypes';

// types
import {
	DocumentFileType,
	DocumentFolder,
	DocumentSourceType,
	DocumentViewMode,
	SystemDocument,
} from './documentTypes';

// design system
import { formatDate } from '../../../../utils/dates';
import { radius, spacing } from '../../../../utils/designSystem';

// Constants

const PAGE_SIZE = 10;

// Source type configuration
const SOURCE_TYPE_CONFIG: Record<
	DocumentSourceType,
	{ label: string; icon: typeof IconContract; color: string }
> = {
	contract: { label: 'Contract', icon: IconContract, color: '#2563eb' },
	invoice: { label: 'Invoice', icon: IconInvoice, color: '#ea580c' },
	transaction: { label: 'Transaction', icon: IconTransaction, color: '#16a34a' },
	payment: { label: 'Payment', icon: IconPayment, color: '#7c3aed' },
	activity: { label: 'Activity', icon: IconActivity, color: '#d97706' },
	beneficiary: { label: 'Beneficiary', icon: IconBeneficiary, color: '#0d9488' },
};

// Processing stage labels (matches DialogDocumentViewer)
const STAGE_LABELS: Record<string, { label: string; color: string }> = {
	silver: { label: 'Parsed', color: '#16a34a' },
	pregold: { label: 'Enriched', color: '#16a34a' },
	gold: { label: 'AI Analysis', color: '#d97706' },
};

/**
 * Get a colored file-type icon. Supports the typed `DocumentFileType` enum,
 * raw extension strings, AND full filenames (extracts extension).
 */
const getFileTypeIcon = (fileTypeOrName: DocumentFileType | string) => {
	// Extract extension if it looks like a filename (contains a dot)
	let lower = (fileTypeOrName || '').toLowerCase();
	if (lower.includes('.')) {
		lower = lower.split('.').pop() || '';
	}

	// PDF
	if (lower === 'pdf') return <IconPdf sx={{ color: '#d32f2f', fontSize: 24 }} />;

	// Word documents
	if (['doc', 'docx', 'word', 'rtf', 'odt'].includes(lower))
		return <IconFile sx={{ color: '#2563eb', fontSize: 24 }} />;

	// Spreadsheets
	if (['xlsx', 'xls', 'csv', 'excel', 'ods'].includes(lower))
		return <IconXlsx sx={{ color: '#16a34a', fontSize: 24 }} />;

	// Images
	if (['image', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff'].includes(lower))
		return <IconImage sx={{ color: '#ea580c', fontSize: 24 }} />;

	// Default
	return <IconFile sx={{ color: '#6b7280', fontSize: 24 }} />;
};

// Filter Configs

const SOURCE_FILTER_OPTIONS = [
	{ value: 'all', label: 'All Sources' },
	{ value: 'contract', label: 'Contracts' },
	{ value: 'invoice', label: 'Invoices' },
	{ value: 'transaction', label: 'Transactions' },
	{ value: 'payment', label: 'Payments' },
	{ value: 'activity', label: 'Activities' },
	{ value: 'beneficiary', label: 'Beneficiaries' },
];

const FILE_TYPE_FILTER_OPTIONS = [
	{ value: 'all', label: 'All Types' },
	{ value: 'pdf', label: 'PDF' },
	{ value: 'doc', label: 'Word' },
	{ value: 'xlsx', label: 'Excel' },
	{ value: 'image', label: 'Images' },
	{ value: 'other', label: 'Other' },
];

const DATE_FILTER_OPTIONS = [
	{ value: 'all', label: 'All Time' },
	{ value: '7d', label: 'Last 7 Days' },
	{ value: '30d', label: 'Last 30 Days' },
	{ value: '90d', label: 'Last 90 Days' },
	{ value: '1y', label: 'Last Year' },
];

// Sort State

// DataTable Column Definitions

const buildColumns = (
	t: (key: string) => string,
	renderSourceChip: (sourceType: DocumentSourceType) => React.ReactNode,
	getFileTypeIcon: (fileType: DocumentFileType | string) => React.ReactNode,
	documentsWithProcessing: Set<string>,
	processingStages: Map<string, Set<string>>,
	onOpenViewer: (doc: SystemDocument, e: React.MouseEvent) => void,
	onOpenInsights: (doc: SystemDocument, e: React.MouseEvent) => void
): ColumnDef<SystemDocument>[] => [
	{
		id: 'fileIcon',
		label: '',
		minWidth: 40,
		sortable: false,
		render: (row) => getFileTypeIcon(row.name),
	},
	{
		id: 'name',
		label: t('spendPlan.documents.columnName'),
		minWidth: 320,
		sortable: true,
		render: (row) => {
			const jobId = row.jobId;
			const hasJob = !!jobId;
			const stages = jobId ? processingStages.get(jobId) : undefined;
			const hasGold = stages?.has('gold') ?? false;
			const isProcessing = hasJob && !hasGold && documentsWithProcessing.has(jobId);
			return (
				<Stack direction="row" alignItems="center" spacing={1}>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Typography variant="body2" sx={{ fontWeight: 500 }}>
							{row.name}
						</Typography>
						{row.description && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{
									display: 'block',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									maxWidth: 300,
								}}>
								{row.description}
							</Typography>
						)}
						{/* Processing stage pills — show whenever doc has a jobId */}
						{hasJob && (
							<Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
								{(['silver', 'pregold', 'gold'] as const).map((s, i) => {
									const stageConfig = STAGE_LABELS[s];
									const isComplete = stages?.has(s) ?? false;
									const isPending = !isComplete && isProcessing;
									return (
										<Tooltip
											key={s}
											title={`${stageConfig.label}: ${isComplete ? 'Complete' : isProcessing ? 'Processing…' : 'Pending'}`}
											arrow
											placement="top">
											<Box
												sx={{
													position: 'relative',
													width: 8,
													height: 8,
													borderRadius: '50%',
													bgcolor: isComplete ? stageConfig.color : 'action.disabled',
													transition: 'background-color 0.3s',
													cursor: 'help',
													// Ping animation on pending dots while processing
													...(isPending
														? {
																'&::after': {
																	content: '""',
																	position: 'absolute',
																	inset: -2,
																	borderRadius: '50%',
																	border: '1.5px solid',
																	borderColor: 'text.disabled',
																	animation: `stagePing 1.8s ease-out infinite`,
																	animationDelay: `${i * 0.3}s`,
																	opacity: 0,
																	'@keyframes stagePing': {
																		'0%': { transform: 'scale(0.8)', opacity: 0.8 },
																		'70%': { transform: 'scale(2)', opacity: 0 },
																		'100%': { transform: 'scale(2)', opacity: 0 },
																	},
																},
															}
														: {}),
												}}
											/>
										</Tooltip>
									);
								})}
							</Stack>
						)}
					</Box>
					<Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
						<Tooltip title="Open in Document Viewer">
							<IconButton
								size="small"
								onClick={(e) => onOpenViewer(row, e)}
								sx={{
									color: 'primary.main',
									'&:hover': { bgcolor: 'action.hover' },
								}}>
								<IconOpenDocument sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
						<Tooltip
							title={
								isProcessing
									? 'Processing…'
									: hasGold
										? 'View AI Insights'
										: hasJob
											? 'Awaiting AI processing'
											: 'No AI insights available'
							}>
							<span>
								<IconButton
									disabled={!hasJob}
									size="small"
									onClick={(e) => onOpenInsights(row, e)}
									sx={{
										color: hasGold ? '#7c3aed' : hasJob ? alpha('#7c3aed', 0.4) : 'text.disabled',
										'&:hover': hasJob ? { bgcolor: 'rgba(124, 58, 237, 0.08)' } : undefined,
										...(isProcessing
											? {
													animation: 'pulse 1.5s ease-in-out infinite',
													'@keyframes pulse': {
														'0%, 100%': { opacity: 1 },
														'50%': { opacity: 0.4 },
													},
												}
											: {}),
									}}>
									<IconAI sx={{ fontSize: 18 }} />
								</IconButton>
							</span>
						</Tooltip>
					</Stack>
				</Stack>
			);
		},
	},
	{
		id: 'sourceType',
		label: t('spendPlan.documents.columnSource'),
		minWidth: 120,
		sortable: true,
		render: (row) => renderSourceChip(row.sourceType),
	},
	{
		id: 'sourceLabel',
		label: t('spendPlan.documents.columnSourceRef'),
		minWidth: 140,
		sortable: false,
		render: (row) => (
			<Typography variant="body2" color="text.secondary">
				{row.sourceLabel}
			</Typography>
		),
	},
	{
		id: 'uploadedAt',
		label: t('spendPlan.documents.columnUploaded'),
		minWidth: 120,
		sortable: true,
		render: (row) => (
			<Typography variant="body2" color="text.secondary">
				{formatDate({ date: row.uploadedAt })}
			</Typography>
		),
	},
	{
		id: 'uploadedBy',
		label: t('spendPlan.documents.columnUploadedBy'),
		minWidth: 140,
		sortable: false,
		render: (row) => (
			<Typography variant="body2" color="text.secondary">
				{row.uploadedBy}
			</Typography>
		),
	},
	{
		id: 'size',
		label: t('spendPlan.documents.columnSize'),
		minWidth: 80,
		sortable: true,
		sortKey: 'sizeBytes',
		render: (row) => (
			<Typography variant="body2" color="text.secondary">
				{row.size}
			</Typography>
		),
	},
];

// Component

export const Documents = () => {
	// hooks
	const { t } = useTranslation();
	const theme = useTheme();
	const [searchParams, setSearchParams] = useSearchParams();

	// project context — used for document isolation
	const { project } = useContext(ContextProject);
	const projectId = project?.id;

	// =========================================================================
	// URL params → single source of truth for query state
	// =========================================================================
	const search = searchParams.get('search') || '';
	const sourceType = searchParams.get('source_type') || 'all';
	const fileType = searchParams.get('doc_type') || 'all';
	const dateRange = searchParams.get('date_range') || 'all';
	const orderBy = searchParams.get('order_by') || 'uploadedAt';
	const order = (searchParams.get('order') || 'DESC') as DTSortDirection;
	const page = Math.max(1, Number(searchParams.get('page') || '1'));
	const rowsPerPage = Math.min(
		100,
		Math.max(1, Number(searchParams.get('page_size') || String(PAGE_SIZE)))
	);
	const viewMode = (searchParams.get('view') || 'list') as DocumentViewMode;
	const docId = searchParams.get('doc_id') || undefined;
	const docTab = (searchParams.get('doc_tab') || 'document') as 'document' | 'insights';
	const docEdit = searchParams.get('doc_edit') === 'true';

	/** Helper to update one or more URL params (resets page to 1 by default) */
	const updateParams = useCallback(
		(updates: Record<string, string | number | undefined>, resetPage = true) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					Object.entries(updates).forEach(([key, value]) => {
						if (value === undefined || value === '' || value === 'all') {
							next.delete(key);
						} else {
							next.set(key, String(value));
						}
					});
					if (resetPage && !('page' in updates)) {
						next.delete('page');
					}
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	// Convenience wrappers for filter/search values as the shape DataTable expects
	const filterValues: Record<string, string | number> = useMemo(
		() => ({ sourceType, fileType, dateRange }),
		[sourceType, fileType, dateRange]
	);

	// AI Documents API hook
	const {
		data: documents,
		loading,
		error: apiError,
		pagination: apiPagination,
		stats: apiStats,
		folders: apiFolders,
		fetchWithQuery,
	} = useAiDocuments({ isEnabled: false, projectId }); // manual fetching — driven by URL params

	// Local-only state (dialogs, folders)
	const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

	// Document preview dialog
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewIndex, setPreviewIndex] = useState(0);
	const [previewDocuments, setPreviewDocuments] = useState<SystemDocument[]>([]);

	// AI Insights dialog - state removed, now integrated in DialogDocumentViewer

	// Upload dialog
	const [uploadOpen, setUploadOpen] = useState(false);

	// AI processing stage tracking (job_id → Set of completed stages)
	const [processingStages, setProcessingStages] = useState<Map<string, Set<string>>>(new Map());
	// Set of job_ids that are actively processing (SSE events received but gold not yet complete)
	const [documentsWithProcessing, setDocumentsWithProcessing] = useState<Set<string>>(new Set());

	// Cache of full blob metadata from bulk fetch — avoids second API call when opening insights
	const blobsCache = useRef<Map<string, AiDocumentResultBlob[]>>(new Map());

	/** Re-fetch documents using current URL params */
	const refetchDocuments = useCallback(() => {
		fetchWithQuery({
			search: search || undefined,
			source_type: sourceType !== 'all' ? sourceType : undefined,
			doc_type: fileType !== 'all' ? fileType : undefined,
			date_range: dateRange !== 'all' ? dateRange : undefined,
			order_by: SORT_KEY_MAP[orderBy] || 'uploaded_at',
			order,
			page,
			page_size: rowsPerPage,
			group_by: viewMode === 'folder' ? 'folder' : undefined,
			populate: 'uploaded_by',
		});
	}, [
		fetchWithQuery,
		search,
		sourceType,
		fileType,
		dateRange,
		orderBy,
		order,
		page,
		rowsPerPage,
		viewMode,
	]);

	/** Callback when viewer edit mode changes — sync doc_edit URL param */
	const handleEditChange = useCallback(
		(editing: boolean) => {
			updateParams({ doc_edit: editing ? 'true' : undefined }, false);
		},
		[updateParams]
	);

	/** Save handler for inline viewer edit */
	const handleViewerEditSave = useCallback(
		async (payload: { id: string; tags: string[] }) => {
			await updateAiDocument({
				id: payload.id,
				tags: payload.tags,
			});
			refetchDocuments();
		},
		[refetchDocuments]
	);

	// =========================================================================
	// SSE: Subscribe to docAI notifications for real-time processing updates
	// =========================================================================
	const handleDocAINotification = useCallback((notification: SSENotification) => {
		const jobId = notification.transactionId;
		// Stage can be at payload.stage or nested inside payload.message.stage
		const msg = notification.payload?.message;
		const stage = (notification.payload?.stage ??
			(typeof msg === 'object' ? (msg as Record<string, unknown>)?.stage : undefined)) as
			| string
			| undefined;

		if (!jobId || !stage) return;

		// Track the completed stage (normalize pre-gold -> pregold)
		const normalizedStage = stage === 'pre-gold' ? 'pregold' : stage;
		setProcessingStages((prev) => {
			const next = new Map(prev);
			const stageSet = new Set(prev.get(jobId) || []);
			stageSet.add(normalizedStage);
			next.set(jobId, stageSet);
			return next;
		});

		// Track that this document is actively being processed
		setDocumentsWithProcessing((prev) => {
			const next = new Set(prev);
			next.add(jobId);
			return next;
		});
	}, []);

	useSSE({
		filter: (n: SSENotification) => n.type === 'docAI',
		onNotification: handleDocAINotification,
	});

	// Seed processing stages from documents that have job_ids
	// (bulk-fetch existing results on first load so we show stage dots for already-processed docs)
	const seededJobIds = useRef<Set<string>>(new Set());

	useEffect(() => {
		const jobIds = documents
			.map((d) => d.jobId)
			.filter((id): id is string => !!id && !seededJobIds.current.has(id));

		if (jobIds.length === 0) return;

		// Mark as seeded to avoid re-fetching
		jobIds.forEach((id) => seededJobIds.current.add(id));

		// Bulk-fetch results for all job_ids in a single request
		fetchAiDocumentResultsBulk(jobIds)
			.then((res) => {
				setProcessingStages((prev) => {
					const next = new Map(prev);
					Object.entries(res.results).forEach(([jobId, data]) => {
						if (data.blobs.length > 0) {
							// Normalize pre-gold -> pregold to match STAGE_LABELS
							const stageNames = new Set(
								data.blobs.map((b) => (b.stage === 'pre-gold' ? 'pregold' : b.stage))
							);
							next.set(jobId, stageNames);
							// Also cache full blobs for later use by insights dialog
							blobsCache.current.set(jobId, data.blobs);
						}
					});
					return next;
				});

				// Mark docs with a jobId that haven't reached gold as actively processing.
				// This covers freshly uploaded files before any SSE stage events arrive.
				setDocumentsWithProcessing((prev) => {
					const next = new Set(prev);
					jobIds.forEach((id) => {
						const data = res.results[id];
						const stages = data?.blobs?.map((b) => b.stage) ?? [];
						const hasGold = stages.includes('gold');
						if (!hasGold) next.add(id);
					});
					return next;
				});
			})
			.catch(() => {
				// No results yet — mark all as processing (freshly uploaded)
				setDocumentsWithProcessing((prev) => {
					const next = new Set(prev);
					jobIds.forEach((id) => next.add(id));
					return next;
				});
			});
	}, [documents]);

	// Debounce ref for search
	const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// =========================================================================
	// Fetch from API whenever URL params change
	// =========================================================================
	useEffect(() => {
		refetchDocuments();
	}, [refetchDocuments]);

	// Summary stats from API (or sensible defaults)
	const stats = useMemo(() => {
		if (!apiStats) {
			return {
				totalDocuments: 0,
				totalSize: '0 B',
				recentUploads: 0,
				bySourceType: {} as Record<DocumentSourceType, number>,
			};
		}
		const totalBytes = Number(apiStats.total_size_bytes) || 0;
		const formatFileSize = (bytes: number): string => {
			if (bytes === 0) return '0 B';
			const k = 1024;
			const sizes = ['B', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
		};
		return {
			totalDocuments: apiStats.total_documents,
			totalSize: formatFileSize(totalBytes),
			recentUploads: apiStats.recent_uploads_7d,
			bySourceType: {} as Record<DocumentSourceType, number>,
		};
	}, [apiStats]);

	// Total count from API pagination (for DataTable)
	const totalCount = apiPagination?.total_count ?? documents.length;

	// Document folders — from API when available, falling back to local grouping
	const folders = useMemo(() => {
		// Use API folders if available (group_by=folder response)
		if (apiFolders.length > 0) return apiFolders;

		// Fallback: group the current flat documents list into folders client-side
		if (documents.length === 0) return [];

		const map = new Map<string, DocumentFolder>();
		for (const doc of documents) {
			const key = `${doc.sourceType}-${doc.sourceId}`;
			if (!map.has(key)) {
				map.set(key, {
					id: key,
					name: doc.sourceLabel || `${doc.sourceType} ${doc.sourceId}`,
					sourceType: doc.sourceType,
					sourceId: doc.sourceId,
					documentCount: 0,
					totalSize: '0 B',
					documents: [],
					lastUpdated: doc.uploadedAt,
				});
			}
			const folder = map.get(key);
			if (!folder) continue;
			folder.documents.push(doc);
			folder.documentCount = folder.documents.length;
			if (doc.uploadedAt > folder.lastUpdated) {
				folder.lastUpdated = doc.uploadedAt;
			}
		}
		return Array.from(map.values());
	}, [apiFolders, documents]);

	// Handlers
	const handleViewModeChange = (
		_: React.MouseEvent<HTMLElement>,
		newMode: DocumentViewMode | null
	) => {
		if (newMode) {
			updateParams({ view: newMode === 'list' ? undefined : newMode });
		}
	};

	const handleSortChange = useCallback(
		(newOrderBy: string, newOrder: DTSortDirection) => {
			updateParams({ order_by: newOrderBy, order: newOrder });
		},
		[updateParams]
	);

	const handleFilterChange = useCallback(
		(key: string, value: string | number) => {
			// Map UI filter keys to URL param keys
			const paramKeyMap: Record<string, string> = {
				sourceType: 'source_type',
				fileType: 'doc_type',
				dateRange: 'date_range',
			};
			updateParams({ [paramKeyMap[key] || key]: value });
		},
		[updateParams]
	);

	/** Debounced search — updates URL after 300ms of inactivity */
	const handleSearchChange = useCallback(
		(value: string) => {
			if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
			searchTimerRef.current = setTimeout(() => {
				updateParams({ search: value || undefined });
			}, 300);
		},
		[updateParams]
	);

	const handlePageChange = useCallback(
		(newPage: number) => {
			updateParams({ page: newPage > 1 ? newPage : undefined }, false);
		},
		[updateParams]
	);

	const handleRowsPerPageChange = useCallback(
		(newSize: number) => {
			updateParams({ page_size: newSize !== PAGE_SIZE ? newSize : undefined });
		},
		[updateParams]
	);

	const handleDocumentClick = useCallback(
		(doc: SystemDocument, documentList: SystemDocument[]) => {
			setPreviewDocuments(documentList);
			const index = documentList.findIndex((d) => d.id === doc.id);
			setPreviewIndex(index >= 0 ? index : 0);
			updateParams({ doc_id: doc.id, doc_tab: 'document' }, false);
		},
		[updateParams]
	);

	const handleOpenViewerFromButton = useCallback(
		(doc: SystemDocument, e: React.MouseEvent) => {
			e.stopPropagation();
			handleDocumentClick(doc, documents);
		},
		[documents, handleDocumentClick]
	);

	const handleOpenInsights = useCallback(
		(doc: SystemDocument, e: React.MouseEvent) => {
			e.stopPropagation();
			if (!doc.jobId) return;

			// Find this document in the current list and set up preview state
			const index = documents.findIndex((d) => d.id === doc.id);
			setPreviewDocuments(documents);
			setPreviewIndex(index >= 0 ? index : 0);
			// Navigate via URL params with insights tab
			updateParams({ doc_id: doc.id, doc_tab: 'insights' }, false);
		},
		[documents, updateParams]
	);

	// Sync previewOpen state with URL params
	useEffect(() => {
		setPreviewOpen(!!docId);
	}, [docId]);

	// Handle closing the document viewer (clears URL params)
	const handleCloseViewer = useCallback(() => {
		updateParams({ doc_id: undefined, doc_tab: undefined, doc_edit: undefined }, false);
	}, [updateParams]);

	const toggleFolder = useCallback((folderId: string) => {
		setExpandedFolders((prev) => {
			const next = new Set(prev);
			if (next.has(folderId)) {
				next.delete(folderId);
			} else {
				next.add(folderId);
			}
			return next;
		});
	}, []);

	// Map SystemDocument to ContractDocument for the existing DialogDocumentViewer
	const mapToViewerDocuments = (docs: SystemDocument[]) =>
		docs.map((doc) => ({
			id: doc.id,
			name: doc.name,
			category: (doc.category === 'contract' ||
			doc.category === 'compliance' ||
			doc.category === 'delivery' ||
			doc.category === 'invoice'
				? doc.category
				: 'other') as 'contract' | 'compliance' | 'delivery' | 'invoice' | 'other',
			type: doc.fileType as 'pdf' | 'doc' | 'image' | 'xlsx',
			size: doc.size,
			uploadedAt: doc.uploadedAt,
			uploadedBy: doc.uploadedBy,
		}));

	// Render source type chip
	const renderSourceChip = (sourceType: DocumentSourceType) => {
		const config = SOURCE_TYPE_CONFIG[sourceType];
		return (
			<Chip
				icon={<config.icon sx={{ fontSize: '14px !important' }} />}
				label={config.label}
				size="small"
				sx={{
					bgcolor: alpha(config.color, 0.1),
					color: config.color,
					fontWeight: 500,
					height: 24,
					'& .MuiChip-icon': {
						color: config.color,
					},
				}}
			/>
		);
	};

	// DataTable columns
	const columns = useMemo(
		() =>
			buildColumns(
				t,
				renderSourceChip,
				getFileTypeIcon,
				documentsWithProcessing,
				processingStages,
				handleOpenViewerFromButton,
				handleOpenInsights
			),
		[t, documentsWithProcessing, processingStages, handleOpenViewerFromButton, handleOpenInsights]
	);

	// DataTable row actions
	const rowActions: RowAction<SystemDocument>[] = useMemo(
		() => [
			{
				label: 'Edit',
				icon: <IconEdit sx={{ fontSize: 18 }} />,
				onClick: (row) => updateParams({ doc_id: row.id, doc_edit: 'true' }, false),
			},
			{
				label: t('spendPlan.documents.download'),
				icon: <IconDownload sx={{ fontSize: 18 }} />,
				onClick: (_row) => {
					// TODO: trigger download
				},
			},
		],
		[t, updateParams]
	);

	// DataTable filter configs
	const dtFilterConfigs: DTFilterConfig[] = useMemo(
		() => [
			{
				key: 'sourceType',
				label: t('spendPlan.documents.filterSource'),
				options: SOURCE_FILTER_OPTIONS,
			},
			{
				key: 'fileType',
				label: t('spendPlan.documents.filterFileType'),
				options: FILE_TYPE_FILTER_OPTIONS,
			},
			{
				key: 'dateRange',
				label: t('spendPlan.documents.filterDateRange'),
				options: DATE_FILTER_OPTIONS,
			},
		],
		[t]
	);

	// =========================================================================
	// Render: List View (using DataTable)
	// =========================================================================
	const renderListView = () => (
		<DataTable<SystemDocument>
			data={documents}
			columns={columns}
			getRowKey={(doc) => doc.id}
			emptyMessage={apiError || t('spendPlan.documents.noDocuments')}
			emptyContent={search ? t('spendPlan.documents.noSearchResults') : undefined}
			emptyIcon={<IconDocument sx={{ fontSize: 48, color: 'text.disabled' }} />}
			orderBy={orderBy}
			order={order}
			onSortChange={handleSortChange}
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={handlePageChange}
			onRowsPerPageChange={handleRowsPerPageChange}
			searchPlaceholder={t('spendPlan.documents.searchPlaceholder')}
			searchValue={search}
			onSearchChange={handleSearchChange}
			showFilters
			filters={dtFilterConfigs}
			filterValues={filterValues}
			onFilterChange={handleFilterChange}
			onRowClick={(doc) => handleDocumentClick(doc, documents)}
			rowActions={rowActions}
			loading={loading}
		/>
	);

	// =========================================================================
	// Render: Folder View
	// =========================================================================
	const renderFolderView = () => (
		<Box>
			{/* Search & Filters for folder view */}
			<Box sx={{ py: spacing.sm }}>
				<SearchFilterBar
					searchPlaceholder={t('spendPlan.documents.searchPlaceholder')}
					searchValue={search}
					onSearchChange={handleSearchChange}
					filters={[
						{
							key: 'sourceType',
							label: t('spendPlan.documents.filterSource'),
							options: SOURCE_FILTER_OPTIONS,
						},
					]}
					filterValues={filterValues}
					onFilterChange={handleFilterChange}
					resultsCount={folders.length}
					totalCount={totalCount}
				/>
			</Box>

			{/* Folder cards */}
			<Box sx={{ pb: spacing.md }}>
				<Grid container spacing={2}>
					{folders.map((folder) => {
						const isExpanded = expandedFolders.has(folder.id);
						const config = SOURCE_TYPE_CONFIG[folder.sourceType];

						return (
							<Grid item xs={12} key={folder.id}>
								<Card
									variant="outlined"
									sx={{
										borderRadius: radius.md,
										borderLeft: `3px solid ${config.color}`,
										overflow: 'hidden',
									}}>
									{/* Folder Header */}
									<CardActionArea onClick={() => toggleFolder(folder.id)}>
										<CardContent sx={{ py: 1.5, px: 2 }}>
											<Stack direction="row" alignItems="center" justifyContent="space-between">
												<Stack direction="row" alignItems="center" spacing={1.5}>
													{isExpanded ? (
														<IconFolderOpen sx={{ color: config.color, fontSize: 24 }} />
													) : (
														<IconFolder sx={{ color: config.color, fontSize: 24 }} />
													)}
													<Box>
														<Typography variant="body2" sx={{ fontWeight: 600 }}>
															{folder.name}
														</Typography>
														<Stack direction="row" spacing={1} alignItems="center">
															{renderSourceChip(folder.sourceType)}
															<Typography variant="caption" color="text.secondary">
																{folder.documentCount}{' '}
																{folder.documentCount === 1 ? 'document' : 'documents'}
																{' · '}
																{folder.totalSize}
															</Typography>
														</Stack>
													</Box>
												</Stack>

												<Typography variant="caption" color="text.secondary">
													{t('spendPlan.documents.lastUpdated')}{' '}
													{formatDate({ date: folder.lastUpdated })}
												</Typography>
											</Stack>
										</CardContent>
									</CardActionArea>

									{/* Expanded Documents */}
									{isExpanded && (
										<Box>
											<Divider />
											<TableContainer>
												<Table size="small">
													<TableBody>
														{folder.documents.map((doc) => (
															<TableRow
																key={doc.id}
																hover
																sx={{
																	cursor: 'pointer',
																	'&:hover': {
																		bgcolor: alpha(theme.palette.primary.main, 0.04),
																	},
																}}
																onClick={() => handleDocumentClick(doc, folder.documents)}>
																<TableCell sx={{ width: 48, pl: 3 }}>
																	{getFileTypeIcon(doc.name)}
																</TableCell>
																<TableCell>
																	<Typography variant="body2" sx={{ fontWeight: 500 }}>
																		{doc.name}
																	</Typography>
																</TableCell>
																<TableCell>
																	<Typography variant="body2" color="text.secondary">
																		{formatDate({ date: doc.uploadedAt })}
																	</Typography>
																</TableCell>
																<TableCell>
																	<Typography variant="body2" color="text.secondary">
																		{doc.uploadedBy}
																	</Typography>
																</TableCell>
																<TableCell>
																	<Typography variant="body2" color="text.secondary">
																		{doc.size}
																	</Typography>
																</TableCell>
																<TableCell sx={{ width: 120 }}>
																	<Stack direction="row" spacing={0.25} justifyContent="flex-end">
																		<Tooltip title="Open in Document Viewer">
																			<IconButton
																				size="small"
																				onClick={(e) => {
																					e.stopPropagation();
																					handleDocumentClick(doc, folder.documents);
																				}}
																				sx={{ color: 'primary.main' }}>
																				<IconOpenDocument sx={{ fontSize: 18 }} />
																			</IconButton>
																		</Tooltip>
																		<Tooltip
																			title={
																				doc.jobId ? 'View AI Insights' : 'No AI insights available'
																			}>
																			<span>
																				<IconButton
																					disabled={!doc.jobId}
																					size="small"
																					onClick={(e) => handleOpenInsights(doc, e)}
																					sx={{
																						color: doc.jobId ? '#7c3aed' : 'text.disabled',
																						'&:hover': doc.jobId
																							? { bgcolor: 'rgba(124, 58, 237, 0.08)' }
																							: undefined,
																					}}>
																					<IconAI sx={{ fontSize: 18 }} />
																				</IconButton>
																			</span>
																		</Tooltip>
																		<Tooltip title={t('spendPlan.documents.download')}>
																			<IconButton
																				size="small"
																				onClick={(e) => {
																					e.stopPropagation();
																				}}>
																				<IconDownload sx={{ fontSize: 18 }} />
																			</IconButton>
																		</Tooltip>
																		<Tooltip title="Edit">
																			<IconButton
																				size="small"
																				onClick={(e) => {
																					e.stopPropagation();
																					updateParams({ doc_id: doc.id, doc_edit: 'true' }, false);
																				}}
																				sx={{ color: 'info.main' }}>
																				<IconEdit sx={{ fontSize: 18 }} />
																			</IconButton>
																		</Tooltip>
																	</Stack>
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										</Box>
									)}
								</Card>
							</Grid>
						);
					})}

					{folders.length === 0 && (
						<Grid item xs={12}>
							<Box sx={{ textAlign: 'center', py: 6 }}>
								<IconFolder sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
								<Typography variant="body2" color="text.secondary">
									{search
										? t('spendPlan.documents.noSearchResults')
										: t('spendPlan.documents.noDocuments')}
								</Typography>
							</Box>
						</Grid>
					)}
				</Grid>
			</Box>
		</Box>
	);

	// =========================================================================
	// Main Render
	// =========================================================================
	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconFolderOpen}
				title={t('spendPlan.documents.title')}
				subtitle={t('spendPlan.documents.subtitle')}
				variant="document"
				stats={[
					{
						label: t('spendPlan.documents.statTotal'),
						value: stats.totalDocuments,
					},
					{
						label: t('spendPlan.documents.statTotalSize'),
						value: stats.totalSize,
					},
					{
						label: t('spendPlan.documents.statRecentUploads'),
						value: stats.recentUploads,
						subValue: t('spendPlan.documents.last7Days'),
					},
				]}
				actions={
					<Stack direction="row" alignItems="center" spacing={2}>
						<Button
							variant="contained"
							startIcon={<IconUpload />}
							onClick={() => setUploadOpen(true)}
							sx={{
								borderRadius: 3,
								fontWeight: 600,
								px: 3,
								py: 1.25,
								textTransform: 'none',
							}}>
							Upload
						</Button>
						<ToggleButtonGroup
							value={viewMode}
							exclusive
							onChange={handleViewModeChange}
							size="small"
							sx={{
								bgcolor: 'background.paper',
								'& .MuiToggleButton-root': {
									textTransform: 'none',
									fontSize: '1.35rem',
									px: 2,
									'&.Mui-selected': {
										bgcolor: alpha(theme.palette.primary.main, 0.1),
										color: 'primary.main',
									},
								},
							}}>
							<ToggleButton value="list">
								<Tooltip title={t('spendPlan.documents.listView')}>
									<IconList sx={{ mr: 0.5, fontSize: 18 }} />
								</Tooltip>
								{t('spendPlan.documents.listView')}
							</ToggleButton>
							<ToggleButton value="folder">
								<Tooltip title={t('spendPlan.documents.folderView')}>
									<IconGrid sx={{ mr: 0.5, fontSize: 18 }} />
								</Tooltip>
								{t('spendPlan.documents.folderView')}
							</ToggleButton>
						</ToggleButtonGroup>
					</Stack>
				}
			/>

			{/* Content */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{viewMode === 'list' ? renderListView() : renderFolderView()}
			</Box>

			{/* Document Preview Dialog */}
			<DialogDocumentViewer
				currentIndex={previewIndex}
				documents={mapToViewerDocuments(previewDocuments)}
				blobFilenames={previewDocuments.map((d) => d.name)}
				documentInfos={previewDocuments.map((d) => ({
					id: d.id,
					name: d.name,
					tags: d.tags || [],
					sourceType: d.sourceType,
					sourceLabel: d.sourceLabel,
					size: d.size,
					uploadedAt: d.uploadedAt,
					uploadedBy: d.uploadedBy,
					jobId: d.jobId,
				}))}
				initialTab={docTab}
				isOpen={previewOpen}
				onClose={handleCloseViewer}
				onNavigate={(index) => {
					setPreviewIndex(index);
					const doc = previewDocuments[index];
					if (doc) {
						updateParams({ doc_id: doc.id }, false);
					}
				}}
				onTabChange={(tab) => updateParams({ doc_tab: tab }, false)}
				onEditSave={handleViewerEditSave}
				initialEditing={docEdit}
				onEditChange={handleEditChange}
			/>

			{/* Upload Document Dialog */}
			<DialogUploadDocument
				isOpen={uploadOpen}
				onClose={() => setUploadOpen(false)}
				onSuccess={refetchDocuments}
				projectId={projectId}
			/>
		</Box>
	);
};
